import React, { useCallback, useMemo } from 'react';

import delve from 'dlv';
import { omit } from 'lodash-es';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { CCPAButtonLink, CCPA_COMPLIANCE } from 'components/footer/shared/ccpa-button-link';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { routes } from 'utils/routing';

const AboutMenuEntriesContainer = styled.div`
  position: relative;
  display: inline-block;
  width: 100%;
  min-height: 100%;
  & > div > div + div {
    border-top: 1px solid ${(p: IListProps) => p.borderColor || Styles.color.grey.five};
  }
  & > div > div > ul {
    list-style: none;
    padding: 1rem 0rem;
    margin: 0;
  }
  & > div > div > h3 + ul {
    padding: 0;
  }
  & > div > div > ul > li {
    margin: 0.5rem 0rem;
    padding: 0;
  }
  & > div > div > ul > li > a {
    text-decoration: none;
    color: ${Styles.color.black};
    :hover,
    :focus {
      border-bottom: 2px solid ${Styles.color.black};
    }
  }
  & > div > div > h3 {
    font-family: ${Styles.fontFamily.brand};
    font-weight: ${Styles.fontWeight.normal};
    text-transform: ${Styles.textTransform.headlines};
    font-size: 1rem;
    margin-top: 1rem;
    margin-bottom: 0;
  }
`;

export interface SingleNode {
  title: string;
  path: string;
}

export interface ArrayNode {
  name: { locale: string };
  pages: [SingleNode];
}

interface IListProps {
  title: string;
  entries: [SingleNode | ArrayNode];
  onDismiss?: () => void;
  borderColor?: string;
  sectionHeadings?: boolean;
  navigate: (to: string) => void;
}

const isAbsoluteUrl = (url: string) => url.startsWith('http') || url.startsWith('//');

const getPaths = (nodes: [SingleNode | ArrayNode]): any => {
  return nodes.map((node: SingleNode | ArrayNode) => {
    const title = delve(node, 'localeTitle.locale') || delve(node, 'title') || '';
    const path = delve(node, 'localePath.locale.current') || delve(node, 'path.current') || '';

    if ((node as SingleNode).path) {
      return {
        title,
        path: delve(node, 'redirectUrl.locale') || path,
      };
    }
    return {
      title: (node as ArrayNode).name?.locale,
      pages: getPaths((node as ArrayNode).pages),
    };
  });
};

const navigateAndTrack = (node: SingleNode, props: IListProps) => {
  return (e: React.MouseEvent) => {
    // handle internal links with `navigate`
    if (!isAbsoluteUrl(node.path)) {
      e.preventDefault();
      const target = node.path;
      props.navigate(target);
      props.onDismiss?.();
    }
    // external links work as usual
  };
};

const ListOfLinks = (props: IListProps) => {
  const enableNutritionExplorerLink = useFlag(LaunchDarklyFlag.ENABLE_NUTRITION_EXPLORER);
  const ENABLE_CCPA_DO_NOT_SELL_BUTTON = useFlag(LaunchDarklyFlag.ENABLE_CCPA_DO_NOT_SELL_BUTTON);
  const navigate = useNavigate();

  const filterMenuPaths = useCallback(
    entries => {
      return entries.filter((entry: SingleNode | ArrayNode) => {
        if (Array.isArray((entry as ArrayNode).pages)) {
          return filterMenuPaths((entry as ArrayNode).pages);
        }
        return ((entry as SingleNode).path || '').includes(routes.nutritionExplorer.slice(1))
          ? enableNutritionExplorerLink
          : true;
      });
    },
    [enableNutritionExplorerLink]
  );

  const paths = useMemo(() => filterMenuPaths(getPaths(props.entries)), [
    props.entries,
    filterMenuPaths,
  ]);

  const renderArray = (arr: [SingleNode], containerProps: IListProps) =>
    arr.map((item: SingleNode) => renderItem(item, containerProps));

  const renderItem = (item: SingleNode, containerProps: IListProps) => {
    // Fix relative paths not having a leading slash
    const node =
      isAbsoluteUrl(item.path) || item.path.startsWith('/')
        ? item
        : { ...item, path: `/${item.path}` };

    const isCCPALink: boolean =
      !!node.path.includes(CCPA_COMPLIANCE) && ENABLE_CCPA_DO_NOT_SELL_BUTTON;

    return (
      <li key={`${node.title}: ${node.path}`}>
        {isCCPALink ? (
          <CCPAButtonLink />
        ) : (
          <a onClick={navigateAndTrack(node, { ...containerProps, navigate })} href={node.path}>
            {node.title}
          </a>
        )}
      </li>
    );
  };

  return (
    <AboutMenuEntriesContainer {...omit(props, 'onDismiss')}>
      <div>
        {paths.map((entry: any, i: number) => {
          const isSection = Array.isArray(entry.pages);
          return (
            <div key={`path-group-${i}`}>
              {isSection && props.sectionHeadings && <h3>{entry.title}</h3>}
              <ul>{isSection ? renderArray(entry.pages, props) : renderItem(entry, props)}</ul>
            </div>
          );
        })}
      </div>
    </AboutMenuEntriesContainer>
  );
};

export default ListOfLinks;
